import { Camp67CompactHeader } from '../components/camp67-header/camp67-header';
import { MetaFunction } from '@remix-run/cloudflare';
import { useSearchParams } from '@remix-run/react';
import { useCallback, useEffect, useState } from 'react';
import { reservationStore } from '../state/reservation-store';
import { parseDate } from '@camp67/model/booking/dates';
import { TentTypeV1 } from '@camp67/model';
import { BookingTimeoutDialog } from '../components/booking-timeout';
import { BookingFormComponent } from '../components/booking-form/booking-form-v3';
import { MobileNavigation } from '../components/camp67-header/navigation';
import { images } from '../constants/images';
import clsx from 'clsx';

export const meta: MetaFunction = () => {
  return [
    { title: 'Book your trip - camp67' },
    {
      name: 'description',
      content:
        'Book now for Kumbh Mela 2025. We offer multiple kinds of tents for travellers, families, and groups.',
    },
    {
      tagName: 'link',
      name: 'canonical',
      href: 'https://camp67.com/reservation',
    },
  ];
};

/**
 * Configurable time limit for a booking in ms. When it ends,
 * the booking form will be cleared and reset.
 *
 * Currently: 30 minutes.
 */
const BOOKING_TIMEOUT_LIMIT = 30 * 60 * 1000;

export default function Reservation() {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const from = searchParams.get('from') as string | null;
    const to = searchParams.get('to') as string | null;

    const type = searchParams.get('tentType') as string | null;

    if (type !== null) {
      const typeDecoded = decodeURIComponent(type);

      reservationStore.clearRooms();
      reservationStore.addRoom(typeDecoded as TentTypeV1);
    }

    const startDate = from ? parseDate(from) : undefined;
    const endDate = to ? parseDate(to) : undefined;

    if (startDate && endDate) {
      reservationStore.chooseDateStart(startDate);
      reservationStore.chooseDateRange(endDate);
    }
  }, [searchParams, setSearchParams]);

  const [isTimerWarningOpen, setIsTimerWarningOpen] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsTimerWarningOpen(true);
    }, BOOKING_TIMEOUT_LIMIT);

    return () => clearTimeout(timeout);
  }, []);

  const onClose = useCallback(() => {
    setIsTimerWarningOpen(false);
  }, []);

  return (
    <div className="bg-yellow-50 min-h-screen">
      <BookingTimeoutDialog isOpen={isTimerWarningOpen} onClose={onClose} />
      <div className="md:hidden flex px-4 justify-between items-center py-2">
        <img
          loading="lazy"
          alt="The logo of camp67, a Kumbh campsite in Prayagraj, India."
          src={images['logo-dark']}
          className={clsx('select-none pointer-events-none w-10')}
        />
        <h1 className="font-serif font-extrabold text-lg">Book a room</h1>
        <MobileNavigation textColor="dark" headerAppearance="subtle" size="compact" />
      </div>
      <div className="m-auto hidden sm:block w-full xl:px-60 lg:px-24 md:px-16 px-8 rounded-b-md border-dotted border-b-black border-b ">
        <Camp67CompactHeader />
      </div>
      <main className="w-full h-full sm:first-line:my-8">
        <section
          id="camp67-booking-experience"
          className="w-full h-full xl:px-60 lg:px-24 md:px-16 px-0 overflow-y-auto"
        >
          <BookingFormComponent />
        </section>
      </main>
    </div>
  );
}
